/**
 * Candidate sector preference step (I4T-127)
 *
 * PURPOSE:
 * The application forms submit over ajax and then redirect to a per-form
 * thank-you page. This module reveals the sector preference step that page
 * renders and posts the chosen sectors to the site module's own endpoint,
 * which queues them onto the Craft Candidate the Cockpit plugin owns. The
 * choice is stored locally only, nothing is pushed back to Cockpit.
 *
 * WHY THE SIGNATURE COMES FROM THE QUERY STRING:
 * The submission does not exist until the submit completes, so the credential
 * cannot be rendered into the page. The site module mints a short-lived signature
 * server-side and appends it to Formie's redirect URL, which is the only
 * carrier that survives the redirect. It is read once here, stripped from the
 * address bar so a copied or reloaded URL cannot replay it, and kept in memory
 * for the length of the visit.
 *
 * ENDPOINT CONTRACT:
 *   POST <data-endpoint>   { "signature": string, "sectors": string[] }
 *   200 { "success": true, "queued": true }
 *   400 bad_request | 403 invalid_signature | 403 expired
 *   422 invalid_sectors | 429 rate_limited
 * CSRF is disabled on the endpoint: the signature is the only credential.
 *
 * All copy lives on data attributes of the step container, so this module
 * carries no translatable strings.
 */

const STEP_SELECTOR = '[data-sector-step]';

/** Query parameter the site module appends to the redirect URL. */
const SIGNATURE_PARAM = 'sectorStep';

/** The signature this page arrived with, per step element. */
const signatures = new WeakMap<HTMLElement, string>();

/** Step elements whose save and skip controls are already wired. */
const wired = new WeakSet<HTMLElement>();

/** Step elements that are done, skipped or expired, so nothing reopens them. */
const settled = new WeakSet<HTMLElement>();

function getMessage(step: HTMLElement, key: string): string {
    return step.dataset[key] ?? '';
}

function setStatus(step: HTMLElement, message: string): void {
    const status = step.querySelector<HTMLElement>('[data-sector-step-status]');
    if (!status) return;

    status.textContent = message;
    status.classList.toggle('hidden', message === '');
}

function setBusy(step: HTMLElement, busy: boolean): void {
    step.setAttribute('aria-busy', busy ? 'true' : 'false');

    step.querySelectorAll<HTMLButtonElement>('[data-sector-step-save], [data-sector-step-skip]')
        .forEach((button) => {
            button.disabled = busy;
        });
}

function hidePanel(step: HTMLElement): void {
    step.querySelector<HTMLElement>('[data-sector-step-panel]')?.classList.add('hidden');
}

function getSelectedSectors(step: HTMLElement): string[] {
    return Array.from(step.querySelectorAll<HTMLInputElement>('[data-sector-step-option]'))
        .filter((input) => input.checked)
        .map((input) => input.value)
        .filter((value) => value !== '');
}

/**
 * Take the signature out of the address bar, leaving every other parameter and
 * the hash in place. A reload or a shared link then lands on a plain thank-you
 * page instead of replaying the step.
 *
 * `replaceState()` throws in a sandboxed iframe and can be blocked by privacy
 * extensions. That is cosmetic: the signature is single use and short lived,
 * and the step has already been revealed by the time this runs. So the failure
 * is swallowed rather than allowed to take the step down with it.
 */
function stripSignatureFromUrl(): void {
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete(SIGNATURE_PARAM);

        const query = url.searchParams.toString();

        window.history.replaceState(
            window.history.state,
            '',
            `${url.pathname}${query === '' ? '' : `?${query}`}${url.hash}`,
        );
    } catch {
        // The URL keeps the parameter. Nothing else changes.
    }
}

function dismiss(step: HTMLElement): void {
    settled.add(step);
    step.classList.add('hidden');
    setStatus(step, '');
}

async function save(step: HTMLElement): Promise<void> {
    const endpoint = step.dataset.endpoint;
    const signature = signatures.get(step);
    const sectors = getSelectedSectors(step);

    // Nothing picked is a skip, not an empty write.
    if (sectors.length === 0) {
        dismiss(step);
        return;
    }

    if (!endpoint || !signature) {
        setStatus(step, getMessage(step, 'messageError'));
        return;
    }

    setBusy(step, true);
    setStatus(step, getMessage(step, 'messageSaving'));

    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({ signature, sectors }),
        });

        if (response.ok) {
            settled.add(step);
            setBusy(step, false);
            hidePanel(step);
            setStatus(step, getMessage(step, 'messageDone'));
            return;
        }

        // A 404 (plugin endpoint not deployed yet) has no JSON body, so the
        // parse is guarded and every unknown shape lands in the generic error.
        const body = await response.json().catch(() => null);
        const error = typeof body?.error === 'string' ? body.error : null;

        setBusy(step, false);

        if (error === 'expired') {
            settled.add(step);
            hidePanel(step);
            setStatus(step, getMessage(step, 'messageExpired'));
            return;
        }

        // No automatic retry: the panel stays open so the visitor decides.
        setStatus(step, getMessage(step, 'messageError'));
    } catch {
        setBusy(step, false);
        setStatus(step, getMessage(step, 'messageError'));
    }
}

function wireControls(step: HTMLElement): void {
    if (wired.has(step)) return;
    wired.add(step);

    step.querySelector<HTMLButtonElement>('[data-sector-step-save]')
        ?.addEventListener('click', () => {
            void save(step);
        });

    step.querySelector<HTMLButtonElement>('[data-sector-step-skip]')
        ?.addEventListener('click', () => {
            dismiss(step);
        });
}

export function initSectorPreference(): void {
    const step = document.querySelector<HTMLElement>(STEP_SELECTOR);

    // The step only renders on a thank-you page, so this is the exit on every
    // other page in the site.
    if (!step || settled.has(step)) return;

    const signature = new URLSearchParams(window.location.search).get(SIGNATURE_PARAM);

    // No signature means this is a plain visit to the page, not a landing after
    // an application submit. The step stays hidden and nothing is requested.
    if (!signature) return;

    signatures.set(step, signature);
    wireControls(step);

    // Revealed without moving focus. This is a fresh page load, not a response
    // to an interaction, so the visitor reads the thank-you copy first and
    // reaches the step as ordinary content further down the page. Stealing
    // focus here would skip past that copy for keyboard and screen reader
    // users alike. The status region below the controls is `aria-live`, so the
    // outcome of a save is still announced.
    step.classList.remove('hidden');

    // Last, so that tidying the address bar can never be what stops the step
    // from appearing.
    stripSignatureFromUrl();
}

if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initSectorPreference);
    } else {
        initSectorPreference();
    }
}
